/**
 * @fileoverview Syndicator Showdown — Master Selection Panel
 * @module components/SelectionPanel
 *
 * The primary player workspace. Renders the race card stack and
 * navigates between races.
 *
 * NOTE: EntryProvider is supplied by the PARENT (SelectionPage.jsx) so
 * that ReviewSlip (the "My Stable Slip" modal), which lives alongside
 * SelectionPanel, shares the same context instance. Do NOT add another
 * EntryProvider here.
 *
 * ─── ARCHITECTURE ─────────────────────────────────────────────────────────────
 *
 * SelectionPage  (mounts EntryProvider, owns ReviewSlip / "My Stable Slip")
 *   └── SelectionPanel  (pure UI — no provider of its own)
 *       ├── BetSlipPanel (reads EntryContext — desktop sticky sidebar only)
 *       ├── StickyTabStrip
 *       ├── RaceCard[]
 *       └── BottomReviewStrip (mobile+desktop sticky bar — the ONE Stable
 *           Slip trigger; there is no separate floating FAB, so it can never
 *           collide with the chat button)
 */

import React, {
  useRef,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';

  import { RaceCard }      from './race/RaceCard';
import { useEntryState } from '../context/EntryContext';
import { QuickPickBar }  from './QuickPickBar';
import './SelectionPanel.css';

// ─── Sticky Tab Strip ─────────────────────────────────────────────────────────
// "All" (default) shows every race stacked vertically. Selecting "Leg N"
// filters the card list down to just that race — a pure render filter,
// not a route/scroll change. See visibleLeg state in SelectionPanelInner.

const StickyTabStrip = ({ races, visibleLeg, onSelectLeg }) => {
  const safeRaces = Array.isArray(races) ? races : [];

  return (
    <div className="ss-tab-strip">
      <div className="ss-tab-scroll-container">
        <button
          className={`ss-tab ${visibleLeg === 'all' ? 'ss-tab--active' : ''}`}
          onClick={() => onSelectLeg('all')}
          aria-label="Show all races"
          aria-current={visibleLeg === 'all' ? 'true' : undefined}
        >
          <span className="ss-tab-leg">All</span>
        </button>
        {safeRaces.map((race) => {
          const isActive = visibleLeg === race.raceNumber;
          return (
            <button
              key={race.id}
              className={`ss-tab ${isActive ? 'ss-tab--active' : ''}`}
              onClick={() => onSelectLeg(race.raceNumber)}
              aria-label={`Show only Leg ${race.raceNumber}: ${race.raceName || ''}`}
              aria-current={isActive ? 'true' : undefined}
            >
              <span className="ss-tab-leg">Leg {race.raceNumber}</span>
              {race.raceName && (
                <span className="ss-tab-name">{race.raceName}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Bottom Review Strip — the ONE Stable Slip trigger ────────────────────────
// Full-width sticky bar (never a floating pill) so it can never collide with
// the chat FAB. Always clickable — even when races still need attention —
// so the player can open the Stable Slip to see exactly what's short and fix
// it there, rather than being locked out of the review screen entirely.

const BottomReviewStrip = ({ races, onReview }) => {
  const safeRaces = Array.isArray(races) ? races : [];
  const {
    complianceSummary,
    financialAudit,
    totalStartingCapital,
  } = useEntryState();

  const totalRaces = safeRaces.length;

  const compliantCount = useMemo(() => {
    return safeRaces.filter((race) => complianceSummary[race.id]?.isCompliant).length;
  }, [safeRaces, complianceSummary]);

  const allCompliant = compliantCount === totalRaces;
  const spendPercent = totalStartingCapital > 0
    ? Math.round((financialAudit.totalSpent / totalStartingCapital) * 100)
    : 0;

  return (
    <div className="ss-bottom-strip">

      <div className="ss-bottom-progress-bar">
        <div className="ss-bottom-progress-fill" style={{ width: `${spendPercent}%` }} />
      </div>

      <div className="ss-bottom-content">

        <div className="ss-bottom-stat">
          <span className="ss-bottom-label">Stable Slip</span>
          <span
            className="ss-bottom-value"
            style={{ color: allCompliant ? 'var(--accent-green)' : 'var(--accent-amber)' }}
          >
            {compliantCount}<span className="ss-bottom-denom">/{totalRaces}</span>
          </span>
        </div>

        <div className="ss-bottom-stat ss-bottom-stat--center">
          <span className="ss-bottom-label">Total Spend</span>
          <span className="ss-bottom-value">
            ${Math.round(financialAudit.totalSpent).toLocaleString('en-AU')}
          </span>
        </div>

        <div className="ss-bottom-stat">
          <span className="ss-bottom-label">Remaining</span>
          <span className="ss-bottom-value">
            ${Math.round(financialAudit.remainingWallet).toLocaleString('en-AU')}
          </span>
        </div>

        <button
          className={`ss-review-btn ${allCompliant ? 'ss-review-btn--ready' : 'ss-review-btn--warn'}`}
          onClick={onReview}
          aria-label={allCompliant
            ? 'Review your stable slip and submit'
            : `${totalRaces - compliantCount} race${totalRaces - compliantCount !== 1 ? 's' : ''} still need attention — open Stable Slip to fix`
          }
        >
          {allCompliant ? 'Review →' : `Fix ${totalRaces - compliantCount} →`}
        </button>

      </div>
    </div>
  );
};

// ─── Scratch Alert Banner — shown above a race card when a pending refund exists
// onRealloc(alert, action) is provided by SelectionPage and handles the CF call
// + page-level result feedback. The banner itself only shows a local loading state.

const ScratchAlertBanner = ({ alert, onRealloc }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async (action) => {
    if (loading) return;
    setLoading(true);
    try {
      await onRealloc(alert, action);
    } finally {
      setLoading(false);
    }
  };

  const scratchedName   = alert.scratchedRunner
    || alert.scratchedRunners?.map(r => r.name || r.id).join(', ')
    || 'A runner';
  const refundFormatted = Math.round(alert.refundAmount || 0).toLocaleString('en-AU');
  const hasOtherRunners = (alert.otherRunnersInRace || 0) > 0;

  return (
    <div style={{
      backgroundColor: 'rgba(246,180,42,0.12)',
      border:          '1px solid #F6B42A',
      borderRadius:    8,
      padding:         16,
      marginBottom:    16,
    }}>
      <div style={{ color: '#F6B42A', fontWeight: 700, marginBottom: 8, fontSize: 14 }}>
        🚨 {scratchedName.toUpperCase()} SCRATCHED — ${refundFormatted} REFUND READY
      </div>
      <p style={{ color: '#D1D5DB', fontSize: 13, margin: '0 0 12px' }}>
        Choose how to allocate your refund before this race jumps,
        or select a runner below normally.
      </p>

      {loading ? (
        <p style={{ color: '#F6B42A', fontSize: 13, margin: 0 }}>⟳ Applying…</p>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {hasOtherRunners && (
            <button
              onClick={() => handleClick('redistribute')}
              style={{
                padding: '8px 14px', backgroundColor: '#22c55e',
                color: '#fff', border: 'none', borderRadius: 6,
                cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}
            >
              Redistribute Across My Runners
            </button>
          )}
          <button
            onClick={() => handleClick('expensive')}
            style={{
              padding: '8px 14px', backgroundColor: '#C9A84C',
              color: '#fff', border: 'none', borderRadius: 6,
              cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}
          >
            Buy Most Expensive Runner
          </button>
          <button
            onClick={() => handleClick('random')}
            style={{
              padding: '8px 14px', backgroundColor: '#3b82f6',
              color: '#fff', border: 'none', borderRadius: 6,
              cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}
          >
            Pick Randomly
          </button>
        </div>
      )}

      <p style={{ color: '#9CA3AF', fontSize: 11, margin: '10px 0 0' }}>
        Or choose any runner below to buy equity manually.
      </p>
    </div>
  );
};

// ─── BetSlipPanel — live race summary using EntryContext ──────────────────────
// Reads complianceSummary and financialAudit from the shared EntryProvider
// (already live — no separate Firestore listener needed).

const BetSlipPanel = ({ races, onNavigate }) => {
  const {
    complianceSummary,
    financialAudit,
    totalStartingCapital,
  } = useEntryState();

  const spent     = Math.round(financialAudit.totalSpent    || 0);
  const remaining = Math.round(financialAudit.remainingWallet || 0);
  const pct = totalStartingCapital > 0
    ? Math.min(100, Math.round((spent / totalStartingCapital) * 100))
    : 0;

  return (
    <div className="ss-betslip">

      <div className="ss-betslip-header">MY STABLE SLIP</div>

      {/* Wallet summary */}
      <div className="ss-betslip-wallet">
        <div className="ss-betslip-balance-row">
          <span>Wallet</span>
          <span style={{ color: '#22c55e', fontWeight: 700 }}>
            ${remaining.toLocaleString('en-AU')}
          </span>
        </div>
        <div className="ss-betslip-balance-row">
          <span>Spent</span>
          <span>${spent.toLocaleString('en-AU')}</span>
        </div>
        <div className="ss-betslip-progress-bar">
          <div style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Race list */}
      {(Array.isArray(races) ? races : []).map(race => {
        const compliance  = complianceSummary[race.id];
        const isCompliant = compliance?.isCompliant;
        const shortfall   = compliance?.floorShortfall ?? 0;
        const raceSpent   = Math.round(compliance?.totalSpentOnRace ?? 0);

        return (
          <div
            key={race.id}
            className="ss-betslip-race"
            onClick={() => onNavigate(race.id)}
          >
            <div className="ss-betslip-race-header">
              <span>{race.raceName || `Race ${race.raceNumber}`}</span>
              <span className={
                isCompliant
                  ? 'ss-badge-compliant'
                  : shortfall > 0
                    ? 'ss-badge-shortfall'
                    : 'ss-badge-empty'
              }>
                {isCompliant
                  ? '✓'
                  : shortfall > 0
                    ? `$${Math.round(shortfall).toLocaleString()} needed`
                    : '—'}
              </span>
            </div>
            <div className="ss-betslip-race-spend">
              ${raceSpent.toLocaleString('en-AU')} spent
            </div>
          </div>
        );
      })}

    </div>
  );
};

// ─── Inner Panel ─────────────────────────────────────────────────────────────

const SelectionPanelInner = ({ races, onReview, slateId, pendingScratchAlerts, onRealloc, onSell, isEntryLocked = false, slateData, initialRaceId, scrollRequest, onResolveAlert }) => {
  const safeRaces = Array.isArray(races) ? races : [];

  // Derived once — used by every RaceCard for the time-based lock check.
  const entryCloseMode   = slateData?.entryCloseMode  || 'first_race';
  const avgSpendPerRace  = slateData?.avgSpendPerRace  || 100_000;
  const kMultiplier      = slateData?.kMultiplier      ?? 2.900;

  // For 'first_race' mode: all races lock at the chronologically EARLIEST jump,
  // regardless of raceNumber order. Use startTimestamp (ms) if present, fall back
  // to scheduledJump (ISO string). After sanitise(), both are strings/numbers.
  const firstRaceJump = useMemo(() => {
    const timestamps = safeRaces
      .map(r => {
        const v = r.startTimestamp ?? r.scheduledJump;
        if (!v) return null;
        const ms = typeof v === 'number' ? v : new Date(v).getTime();
        return isNaN(ms) ? null : ms;
      })
      .filter(ms => ms !== null);
    return timestamps.length > 0 ? Math.min(...timestamps) : null;
  }, [safeRaces]);

  const cardRefs        = useRef({});

  // ── Race tab filter — "All" (default) or a single Leg N ──────────────────
  const [visibleLeg, setVisibleLeg] = useState('all');

  const visibleRaces = useMemo(
    () => safeRaces.filter((race) => visibleLeg === 'all' || race.raceNumber === visibleLeg),
    [safeRaces, visibleLeg]
  );

  // Every race id on the slate — used by RaceCard/BuyModal to compute how
  // much wallet must be reserved for OTHER races' floor requirements.
  const allRaceIds = useMemo(() => safeRaces.map((r) => r.id), [safeRaces]);

  // Initialise refs on races change
  useEffect(() => {
    safeRaces.forEach((race) => {
      if (!cardRefs.current[race.id]) {
        cardRefs.current[race.id] = React.createRef();
      }
    });
  }, [safeRaces]);

  // Deep-link: scroll to a specific race on first render (e.g. ?race=<raceId>)
  const didScrollToInitial = useRef(false);
  useEffect(() => {
    if (!initialRaceId || didScrollToInitial.current || safeRaces.length === 0) return;
    const ref = cardRefs.current[initialRaceId];
    if (!ref?.current) return;
    didScrollToInitial.current = true;
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  }, [initialRaceId, safeRaces]);

  // Shared navigate helper used by both the sidebar and the Stable Slip
  // modal. Falls back to "All" so the target card is guaranteed to be
  // rendered even if a single leg is currently filtered in.
  const navigateToRace = useCallback((raceId) => {
    setVisibleLeg('all');
    setTimeout(() => {
      cardRefs.current[raceId]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, []);

  // Tapping a race inside the Stable Slip sets `scrollRequest` on the parent
  // page; react to it here so the slip (which lives outside this component's
  // DOM subtree) can still drive the card list's scroll position. `nonce`
  // always changes so repeat taps on the same race still scroll.
  const lastScrollNonce = useRef(null);
  useEffect(() => {
    if (!scrollRequest || scrollRequest.nonce === lastScrollNonce.current) return;
    lastScrollNonce.current = scrollRequest.nonce;
    navigateToRace(scrollRequest.raceId);
  }, [scrollRequest, navigateToRace]);

  return (
    <div className="ss-selection-panel">

      {/* Desktop: side-by-side. Mobile: single column */}
      <div className="ss-panel-with-slip">

        {/* ── Cards column ─────────────────────────────────────────────── */}
        <div className="ss-cards-column">

          <StickyTabStrip
            races={safeRaces}
            visibleLeg={visibleLeg}
            onSelectLeg={setVisibleLeg}
          />

          <QuickPickBar
            races={safeRaces}
            avgSpendPerRace={avgSpendPerRace}
            kMultiplier={kMultiplier}
          />

          <div className="ss-cards-scroll-area">
            {visibleRaces.map((race) => {
              const pendingAlert = Array.isArray(pendingScratchAlerts)
                ? pendingScratchAlerts.find(
                    a => a.raceId === race.id && a.allocationStatus === 'pending_choice'
                  )
                : null;

              return (
                <div
                  key={race.id}
                  ref={cardRefs.current[race.id]}
                  className="ss-card-anchor"
                >
                {pendingAlert && onRealloc && (
                  <ScratchAlertBanner
                    key={pendingAlert.id || `${race.id}-alert`}
                    alert={pendingAlert}
                    onRealloc={onRealloc}
                  />
                )}
                  <RaceCard
                    race={race}
                    onSell={onSell}
                    isEntryLocked={isEntryLocked}
                    entryCloseMode={entryCloseMode}
                    firstRaceJump={firstRaceJump}
                    avgSpendPerRace={avgSpendPerRace}
                    kMultiplier={kMultiplier}
                    hasPendingAllocation={!!pendingAlert}
                    onResolveAlert={onResolveAlert}
                    slateData={slateData}
                    allRaceIds={allRaceIds}
                  />
                </div>
              );
            })}
            <div className="ss-bottom-spacer" />
          </div>

        </div>

        {/* ── Desktop BetSlip sidebar — compact summary; opens the full
             Stable Slip modal for detailed review/submit ─────────────── */}
        <div className="ss-betslip-sidebar">
          <BetSlipPanel races={safeRaces} onNavigate={navigateToRace} />
          <button
            type="button"
            className="ss-betslip-review-btn"
            onClick={onReview}
          >
            Review &amp; Submit →
          </button>
        </div>

      </div>

      {/* ── Sticky bottom Stable Slip trigger (mobile + desktop) — the ONLY
           Stable Slip trigger on mobile. No separate floating FAB, so it
           never collides with the chat button. ─────────────────────────── */}
      <BottomReviewStrip races={safeRaces} onReview={onReview} />

    </div>
  );
};

// ─── Public export ────────────────────────────────────────────────────────────
// EntryProvider is supplied by SelectionPage — do NOT add one here.

export const SelectionPanel = ({ racesData, onReview, slateId, pendingScratchAlerts, onRealloc, onSell, isEntryLocked = false, slateData, initialRaceId, scrollRequest, onResolveAlert }) => {
  const safeRaces = Array.isArray(racesData) ? racesData : [];
  return (
    <SelectionPanelInner
      races={safeRaces}
      onReview={onReview}
      slateId={slateId}
      pendingScratchAlerts={pendingScratchAlerts}
      onRealloc={onRealloc}
      onSell={onSell}
      isEntryLocked={isEntryLocked}
      slateData={slateData}
      initialRaceId={initialRaceId}
      scrollRequest={scrollRequest}
      onResolveAlert={onResolveAlert}
    />
  );
};

export default SelectionPanel;
